import { countriesWithAgencyConfig } from 'features/auth/constants';
import { IInvoiceSummary, InvoiceStatusEnum, InvoiceTypeEnum, PaymentReceivedPlatformTypeEnum } from './types';

export const TYPE_LABEL: Record<InvoiceTypeEnum, string> = {
	[InvoiceTypeEnum.Cash]: 'Contado',
	[InvoiceTypeEnum.Credit]: 'Crédito',
	[InvoiceTypeEnum.CreditNote]: 'NC',
};

export const TYPE_COLOR: Record<InvoiceTypeEnum, 'primary' | 'secondary' | 'warning'> = {
	[InvoiceTypeEnum.Cash]: 'primary',
	[InvoiceTypeEnum.Credit]: 'secondary',
	[InvoiceTypeEnum.CreditNote]: 'warning',
};

export const STATUS_LABEL: Record<InvoiceStatusEnum, string> = {
	[InvoiceStatusEnum.Draft]: 'Borrador',
	[InvoiceStatusEnum.Issued]: 'Emitida',
	[InvoiceStatusEnum.Credited]: 'Anulada',
};

export const STATUS_COLOR: Record<InvoiceStatusEnum, 'default' | 'success' | 'error'> = {
	[InvoiceStatusEnum.Draft]: 'default',
	[InvoiceStatusEnum.Issued]: 'success',
	[InvoiceStatusEnum.Credited]: 'error',
};

export const PLATFORM_LABEL: Record<PaymentReceivedPlatformTypeEnum, string> = {
	[PaymentReceivedPlatformTypeEnum.PaymentSimulator]: 'Tarjeta contra Aerolínea',
	[PaymentReceivedPlatformTypeEnum.SiemprePago]: 'SiemprePago',
	[PaymentReceivedPlatformTypeEnum.Bancard]: 'Bancard',
	[PaymentReceivedPlatformTypeEnum.ItauPoints]: 'Puntos Itaú',
	[PaymentReceivedPlatformTypeEnum.ATCRedEnlace]: 'ATC Red Enlace',
	[PaymentReceivedPlatformTypeEnum.Wompi]: 'Wompi',
	[PaymentReceivedPlatformTypeEnum.Cash]: 'Efectivo',
	[PaymentReceivedPlatformTypeEnum.WireTransfer]: 'Transferencia',
};

export const BU_LABEL: Record<number, string> = Object.fromEntries(
	countriesWithAgencyConfig.map((c) => [c.agencyId, c.id as string]),
);

export const formatDate = (iso?: string): string => {
	if (!iso) return '—';
	try {
		return new Date(iso).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric' });
	} catch {
		return iso;
	}
};

export const formatAmount = (amount: number, currency: string): string =>
	`${new Intl.NumberFormat('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
		amount,
	)} ${currency}`;

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

export const isInvoiceOverdue = (invoice: IInvoiceSummary): boolean =>
	invoice.status === InvoiceStatusEnum.Issued && invoice.dueDate < todayIso() && invoice.pendingAmount > 0;

/** Días de vencimiento: usa el overdueDays de la API o lo calcula desde dueDate. */
export const invoiceOverdueDays = (invoice: IInvoiceSummary): number => {
	if (invoice.overdueDays !== undefined) return invoice.overdueDays;
	const msPerDay = 24 * 60 * 60 * 1000;
	return Math.floor((Date.now() - new Date(`${invoice.dueDate}T00:00:00`).getTime()) / msPerDay);
};

/** Saldo pendiente convertido a USD usando el TC de emisión de la factura. */
export const pendingAmountInMainCurrency = (invoice: IInvoiceSummary): number => {
	if (invoice.currency === 'USD') return invoice.pendingAmount;
	if (!invoice.exchangeRate || invoice.exchangeRate <= 0) return 0;
	return Number((invoice.pendingAmount / invoice.exchangeRate).toFixed(2));
};
